import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { geoFrance, professionalProfiles, professionalCategories, serviceCategories, leads } from '@afficheservices/db/schema'
import { eq, sql, and, isNull, desc, count } from 'drizzle-orm'
import { isDomTomDept } from '~/lib/dom-tom'
import { gone } from '~/lib/dom-tom.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { LeadCaptureBlock } from '~/components/lead-capture-block'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import {
  MapPin, Users, Building2, Wrench, ArrowRight, TrendingUp,
  CheckCircle, Clock, Shield, Star, ChevronRight, Activity,
} from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

// ── Top departments for internal linking ──
const TOP_DEPARTMENTS = [
  { code: '75', name: 'Paris' },
  { code: '13', name: 'Bouches-du-Rhône' },
  { code: '69', name: 'Rhône' },
  { code: '31', name: 'Haute-Garonne' },
  { code: '06', name: 'Alpes-Maritimes' },
  { code: '44', name: 'Loire-Atlantique' },
  { code: '33', name: 'Gironde' },
  { code: '59', name: 'Nord' },
  { code: '67', name: 'Bas-Rhin' },
  { code: '34', name: 'Hérault' },
  { code: '35', name: 'Ille-et-Vilaine' },
  { code: '38', name: 'Isère' },
  { code: '83', name: 'Var' },
  { code: '51', name: 'Marne' },
  { code: '74', name: 'Haute-Savoie' },
  { code: '21', name: "Côte-d'Or" },
  { code: '76', name: 'Seine-Maritime' },
  { code: '57', name: 'Moselle' },
  { code: '37', name: 'Indre-et-Loire' },
  { code: '63', name: 'Puy-de-Dôme' },
]

const SERVICE_INFO = [
  { slug: 'plomberie', name: 'Plomberie', icon: '🔧', desc: 'Plombiers, chauffagistes, réparation et installation' },
  { slug: 'electricite', name: 'Électricité', icon: '⚡', desc: 'Électriciens, mise aux normes, dépannage' },
  { slug: 'peinture', name: 'Peinture', icon: '🎨', desc: 'Peintres, ravalement, décoration intérieure' },
  { slug: 'renovation', name: 'Rénovation', icon: '🏗️', desc: 'Rénovation complète, aménagement, extension' },
  { slug: 'menuiserie', name: 'Menuiserie', icon: '🪵', desc: 'Menuisiers, fenêtres, portes, agencement' },
  { slug: 'toiture', name: 'Toiture', icon: '🏠', desc: 'Couvreurs, étanchéité, réparation toiture' },
  { slug: 'serrurerie', name: 'Serrurerie', icon: '🔑', desc: 'Serruriers, blindage, dépannage urgent' },
  { slug: 'climatisation', name: 'Climatisation', icon: '❄️', desc: 'Climaticiens, PAC, entretien' },
  { slug: 'carrelage-sol', name: 'Carrelage', icon: '🔲', desc: 'Carreleurs, pose sol et mur' },
  { slug: 'isolation', name: 'Isolation', icon: '🧱', desc: 'Isolation thermique, ITE, ITI, combles' },
  { slug: 'maconnerie', name: 'Maçonnerie', icon: '🧱', desc: 'Maçons, gros oeuvre, fondations' },
  { slug: 'jardinage', name: 'Jardinage', icon: '🌿', desc: 'Jardiniers, entretien espaces verts' },
]

export async function loader({ params }: LoaderFunctionArgs) {
  const deptCode = params.code ?? ''

  // DOM-TOM departments not served — permanent 410.
  if (isDomTomDept(deptCode)) gone()

  // Get department info from geo table
  const [deptRow] = await db
    .select({
      departmentCode: geoFrance.departmentCode,
      departmentName: geoFrance.departmentName,
      regionName: geoFrance.regionName,
    })
    .from(geoFrance)
    .where(eq(geoFrance.departmentCode, deptCode))
    .limit(1)

  if (!deptRow) {
    throw new Response('Département introuvable', { status: 404 })
  }

  // Count distinct cities in this department
  const [cityCount] = await db
    .select({ count: sql<number>`COUNT(DISTINCT ${geoFrance.communeName})` })
    .from(geoFrance)
    .where(eq(geoFrance.departmentCode, deptCode))

  // Top cities by population
  const topCities = await db
    .select({
      name: geoFrance.communeName,
      population: geoFrance.population,
      postalCode: geoFrance.postalCode,
    })
    .from(geoFrance)
    .where(eq(geoFrance.departmentCode, deptCode))
    .orderBy(sql`${geoFrance.population} DESC NULLS LAST`)
    .limit(20)

  // Pros count in this department
  const [prosCount] = await db
    .select({ count: count() })
    .from(professionalProfiles)
    .where(eq(professionalProfiles.departmentCode, deptCode))

  // Pros count by service category
  const prosByCategory = await db
    .select({
      categoryName: serviceCategories.nameFr,
      categorySlug: serviceCategories.slug,
      count: count(),
    })
    .from(professionalCategories)
    .innerJoin(professionalProfiles, eq(professionalCategories.professionalId, professionalProfiles.id))
    .innerJoin(serviceCategories, eq(professionalCategories.categoryId, serviceCategories.id))
    .where(and(
      eq(professionalProfiles.departmentCode, deptCode),
      isNull(serviceCategories.parentId),
    ))
    .groupBy(serviceCategories.nameFr, serviceCategories.slug)
    .orderBy(sql`count(*) DESC`)
    .limit(12)

  // Recent leads in department
  const recentLeads = await db
    .select({
      city: leads.city,
      categoryName: serviceCategories.nameFr,
      createdAt: leads.createdAt,
    })
    .from(leads)
    .innerJoin(serviceCategories, eq(leads.categoryId, serviceCategories.id))
    .where(eq(leads.departmentCode, deptCode))
    .orderBy(desc(leads.createdAt))
    .limit(6)

  // Nearby departments (exclude current)
  const nearbyDepts = TOP_DEPARTMENTS.filter(d => d.code !== deptCode).slice(0, 8)

  return {
    dept: deptRow,
    deptCode,
    cityCount: cityCount?.count ?? 0,
    topCities,
    prosCount: prosCount?.count ?? 0,
    prosByCategory,
    recentLeads: recentLeads.map(l => ({
      city: l.city,
      categoryName: l.categoryName,
      createdAt: l.createdAt?.toISOString() ?? '',
    })),
    nearbyDepts,
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const d = data?.dept
  if (!d) return [{ title: 'Département | AfficheServices' }]
  return [
    { title: `Artisans ${d.departmentName} (${data?.deptCode}) — Trouvez un pro | AfficheServices` },
    { name: 'description', content: `Trouvez les meilleurs artisans en ${d.departmentName} (${data?.deptCode}). ${data?.prosCount} professionnels vérifiés dans ${data?.cityCount} communes. Devis gratuits en plomberie, électricité, rénovation et plus.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/departement/${data?.deptCode}` },
  ]
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return 'récemment'
  const diff = Date.now() - new Date(dateStr).getTime()
  const hours = Math.floor(diff / 3600000)
  if (hours < 1) return "il y a moins d'1h"
  if (hours < 24) return `il y a ${hours}h`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'hier'
  if (days < 7) return `il y a ${days} jours`
  return `il y a ${Math.floor(days / 7)} sem.`
}

function citySlug(name: string): string {
  return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')
}

export default function DepartementPage() {
  const { dept, deptCode, cityCount, topCities, prosCount, prosByCategory, recentLeads, nearbyDepts } = useLoaderData<typeof loader>()

  const faqs = [
    { question: `Comment trouver un artisan en ${dept.departmentName} ?`, answer: `Sur AfficheServices, comparez les profils de ${prosCount} artisans vérifiés en ${dept.departmentName}. Demandez un devis gratuit et recevez jusqu'à 5 propositions en 48h.` },
    { question: `Quels services sont disponibles en ${dept.departmentName} ?`, answer: `Tous les métiers du bâtiment sont couverts : plomberie, électricité, peinture, rénovation, menuiserie, toiture, serrurerie, climatisation, carrelage, isolation, maçonnerie et jardinage.` },
    { question: `Les artisans sur AfficheServices sont-ils vérifiés ?`, answer: `Oui, chaque artisan est vérifié : SIRET valide, assurance décennale contrôlée, et avis clients authentiques. Les certifications RGE et Qualibat sont affichées sur les profils.` },
  ]

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <FAQSchema items={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Départements', url: 'https://www.afficheservices.fr/departement/75' },
        { name: `${dept.departmentName} (${deptCode})`, url: `https://www.afficheservices.fr/departement/${deptCode}` },
      ]} />

      <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-sky-600 to-blue-700 p-8 text-white">
          <div className="flex items-center gap-2 text-sm text-sky-200">
            <MapPin className="h-4 w-4" /> {dept.regionName}
          </div>
          <h1 className="mt-2 text-2xl font-bold sm:text-3xl">
            Artisans en {dept.departmentName} ({deptCode})
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-sky-100">
            Trouvez les meilleurs artisans et entreprises du bâtiment en {dept.departmentName}.
            Devis gratuits, profils vérifiés, avis clients authentiques.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/devis-gratuit" className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-blue-700 shadow-md hover:bg-blue-50">
              Demander un devis gratuit <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Stats grid */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { icon: Users, label: 'Artisans vérifiés', value: prosCount.toLocaleString('fr-FR'), color: 'text-blue-600 bg-blue-100' },
            { icon: Building2, label: 'Communes couvertes', value: cityCount.toLocaleString('fr-FR'), color: 'text-emerald-600 bg-emerald-100' },
            { icon: Wrench, label: 'Services disponibles', value: `${prosByCategory.length}+`, color: 'text-amber-600 bg-amber-100' },
            { icon: Star, label: 'Note moyenne', value: '4,6/5', color: 'text-purple-600 bg-purple-100' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-border bg-white p-4 shadow-xs">
              <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${stat.color}`}>
                <stat.icon className="h-4 w-4" />
              </div>
              <p className="mt-2 text-lg font-bold text-slate-900">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Services grid */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Services en {dept.departmentName}</h2>
          <p className="mt-1 text-xs text-slate-500">Tous les métiers du bâtiment couverts dans votre département</p>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_INFO.map((svc) => {
              const catData = prosByCategory.find(c => c.categorySlug === svc.slug)
              const prosInCat = catData?.count ?? 0
              return (
                <Link
                  key={svc.slug}
                  to={`/services/${svc.slug}`}
                  className="group flex items-start gap-3 rounded-xl border border-border p-4 hover:border-primary hover:shadow-sm"
                >
                  <span className="text-xl">{svc.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-900 group-hover:text-primary">{svc.name}</h3>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-primary" />
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">{svc.desc}</p>
                    {prosInCat > 0 && (
                      <p className="mt-1.5 text-xs font-medium text-emerald-600">{prosInCat} pro{prosInCat > 1 ? 's' : ''} disponible{prosInCat > 1 ? 's' : ''}</p>
                    )}
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* Pros by category */}
        {prosByCategory.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Artisans par spécialité</h2>
            <div className="mt-4 space-y-2">
              {prosByCategory.map((cat) => (
                <Link
                  key={cat.categorySlug}
                  to={`/services/${cat.categorySlug}`}
                  className="flex items-center justify-between rounded-lg border border-border px-4 py-3 hover:border-primary hover:bg-slate-50"
                >
                  <span className="text-sm font-medium text-slate-700">{cat.categoryName}</span>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                      {cat.count} pro{cat.count > 1 ? 's' : ''}
                    </span>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Top cities */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Principales villes — {dept.departmentName}</h2>
          <p className="mt-1 text-xs text-slate-500">Cliquez sur une ville pour voir les artisans disponibles</p>

          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {topCities.map((city) => (
              <Link
                key={city.name + city.postalCode}
                to={`/travaux/${citySlug(city.name)}`}
                className="group flex items-center justify-between rounded-lg border border-border px-4 py-3 hover:border-primary hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 group-hover:text-primary">{city.name}</p>
                  <p className="text-xs text-slate-400">{city.postalCode}</p>
                </div>
                {city.population && (
                  <span className="text-xs text-slate-400">{Number(city.population).toLocaleString('fr-FR')} hab.</span>
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* Recent activity */}
        {recentLeads.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-500" />
              <h2 className="text-lg font-bold text-slate-900">Demandes récentes</h2>
            </div>
            <p className="mt-1 text-xs text-slate-500">Activité réelle dans votre département</p>
            <div className="mt-4 space-y-2">
              {recentLeads.map((lead, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-700">
                      <span className="font-medium">{lead.categoryName}</span> à <span className="font-medium">{lead.city}</span>
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-slate-400">
                    <Clock className="mr-1 inline h-3 w-3" />{timeAgo(lead.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trust signals */}
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="text-base font-bold text-emerald-900">Pourquoi AfficheServices en {dept.departmentName} ?</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Shield, title: 'Artisans vérifiés', desc: 'SIRET contrôlé, assurance décennale vérifiée, identité confirmée' },
              { icon: Star, title: 'Avis authentiques', desc: 'Seuls les clients ayant réalisé des travaux peuvent noter les artisans' },
              { icon: TrendingUp, title: 'Devis gratuits', desc: 'Jusqu\'à 5 devis de professionnels locaux, sans engagement' },
            ].map((t) => (
              <div key={t.title} className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                  <t.icon className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-emerald-900">{t.title}</h3>
                  <p className="mt-0.5 text-xs text-emerald-700">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes — {dept.departmentName}</h2>
          <div className="mt-4 space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-900">{faq.question}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Nearby departments */}
        <div className="mt-6 rounded-xl border border-border bg-white p-4 shadow-xs">
          <h3 className="text-xs font-semibold text-slate-500">Autres départements</h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {nearbyDepts.map((d) => (
              <Link
                key={d.code}
                to={`/departement/${d.code}`}
                className="rounded-full border border-border px-2.5 py-1 text-[11px] text-slate-600 hover:border-primary hover:text-primary"
              >
                {d.name} ({d.code})
              </Link>
            ))}
          </div>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureBlock serviceName="Travaux" cityName={dept.departmentName} categorySlug="renovation" />
        </div>
      </div>

      <Footer />
    </div>
  )
}
